import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { languages, LanguageService, ThemingService } from '@catch/services';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBrightnessLow, faGlobe, faMagic, faMoon } from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { AuthHeroCarouselComponent } from '../auth-hero-carousel/auth-hero-carousel.component';
import { DropdownComponent } from '../dropdown/dropdown.component';
import { InfoBannerComponent } from '../info-banner/info-banner.component';
import { MeshGradientComponent } from '../mesh-gradient/mesh-gradient.component';

export interface AuthShellMaintenanceInfo {
  date: string;
  time: string;
}

@Component({
  selector: 'ct-lib-auth-shell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    TranslocoPipe,
    FontAwesomeModule,
    DropdownComponent,
    InfoBannerComponent,
    MeshGradientComponent,
    AuthHeroCarouselComponent,
  ],
  templateUrl: './auth-shell.component.html',
  styleUrl: './auth-shell.component.scss',
})
export class AuthShellComponent {
  readonly languageService = inject(LanguageService);
  readonly themingService = inject(ThemingService);

  readonly icons = {
    globe: faGlobe,
    moon: faMoon,
    brightnessLow: faBrightnessLow,
    magic: faMagic,
  };

  readonly languages = languages.filter((lang) => lang.value !== 'fr-FR');

  readonly logoLightSrc = input('./assets/img/logos/titled/dark_titled_dark.png');
  readonly logoDarkSrc = input('./assets/img/logos/titled/white_titled_white.png');
  readonly year = input<number>(new Date().getFullYear());
  readonly version = input<string>('');
  readonly maintenanceInfo = input<AuthShellMaintenanceInfo | null>(null);
  readonly heroCarouselI18nPrefix = input<string>(
    'company-tool.views.authentication.hero.carousel'
  );
  readonly themeMenuLabelKey = input('company-tool.views.authentication.theme.menu-label');
  readonly themeLightKey = input('company-tool.views.authentication.theme.light');
  readonly themeDarkKey = input('company-tool.views.authentication.theme.dark');
  readonly themeSystemKey = input('company-tool.views.authentication.theme.system');
  readonly themeTriggerAriaKey = input('company-tool.views.authentication.theme.aria-trigger');
  readonly maintenanceInfoKey = input('company-tool.views.authentication.maintenance-info');

  /**
   * Sets the auth shell theme mode.
   * @param mode - Theme mode to apply
   */
  setAuthThemeMode(mode: 'light' | 'dark' | 'auto'): void {
    this.themingService.setMode(mode);
  }
}
